import type { FC, ReactElement } from "react"

import { AnimatePresence } from "framer-motion"

import { ETypographyColor, ETypographySize, Typography } from "@/3514/components"
import { type TUseInteractionState, useInteractionState } from "@/3514/hooks"
import { THEME } from "@/3514/theme"
import { withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import type { TEmptyAsyncCallback } from "@/shared/types/functions"

import { CoachingChatRetryIcon as Icon } from "../assets"
import { coachingChatStyles as chatStyles } from "../styles"

type _TProps = { handleAction: TEmptyAsyncCallback; isVisible: boolean }

const {
    conversation: { message: classes }
} = chatStyles

const Retrier: FC<_TProps> = ({ handleAction: action, isVisible }: _TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        isActionAffected,
        actions: { onMouseEnter, onMouseLeave, __setter: handleColor }
    }: TUseInteractionState = useInteractionState()

    const handleButtonAction: ReturnType<typeof withPressHandler> = withPressHandler(async (): Promise<void> => {
        handleColor(true)

        await action()
    })

    return (
        <AnimatePresence mode="wait" initial={false}>
            {isVisible && (
                <div className={classes.retrierContainer()}>
                    <Typography
                        color={ETypographyColor.White}
                        size={ETypographySize.Tiny}
                        text={t("entities.coachingChat.chat.messageRetry")}
                    />

                    <div
                        role="button"
                        tabIndex={0}
                        onClick={handleButtonAction}
                        onKeyDown={handleButtonAction}
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        className={classes.retrierButton()}
                    >
                        <Icon fillColor={isActionAffected ? THEME.COLORS.accent : THEME.COLORS.blue[300]} />
                    </div>
                </div>
            )}
        </AnimatePresence>
    )
}

Retrier.displayName = "CoachingChatMessageRetrier"

export { Retrier as CoachingChatMessageRetrier }
